import * as React from 'react';
// Root font size in px. The base font slides with the screen width (see --uxp-font-size in
// _global.scss), so every rem size grows on large screens. Use this when a library needs px
// numbers (chart font sizes, canvas, stroke widths) so they grow the same way.
const readRootFontSize = () => {
    try {
        return parseFloat(getComputedStyle(document.documentElement).fontSize) || 13;
    }
    catch (_a) {
        return 13;
    }
};
// One window listener shared by every component using the hook.
const listeners = new Set();
let rootFontSize = typeof document !== 'undefined' ? readRootFontSize() : 13;
const onResize = () => {
    const next = readRootFontSize();
    if (next === rootFontSize)
        return;
    rootFontSize = next;
    listeners.forEach(l => l());
};
const subscribe = (listener) => {
    if (listeners.size === 0) {
        rootFontSize = readRootFontSize();
        window.addEventListener('resize', onResize);
    }
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0)
            window.removeEventListener('resize', onResize);
    };
};
/**
 * Converts a rem value to px using the current base font size.
 * Reads the size on each call; inside components prefer `useRemToPx()` so the value updates on resize.
 *
 * @example
 * ```
 * const tickFontSize = remToPx(0.9); // 11.7 at 1440 wide, 31.2 at 3840 wide
 * ```
 * @export
 */
export function remToPx(rem) {
    return rem * readRootFontSize();
}
/**
 * Returns a function that converts rem to px. The component re-renders when the base font
 * size changes (window resize), so chart fonts, canvas sizes and stroke widths follow the
 * screen size like the rest of the UI.
 *
 * @example
 * ```
 * const toPx = useRemToPx();
 * <XAxis tick={{ fontSize: toPx(0.9) }} />
 * ```
 * @export
 */
const useRemToPx = () => {
    const size = React.useSyncExternalStore(subscribe, () => rootFontSize, () => 13);
    return React.useCallback((rem) => rem * size, [size]);
};
export default useRemToPx;
